import { useState, useEffect } from "react";
import {
    Container,
    Paper,
    Text,
    Title,
    Group,
    Stack,
    Switch,
    Divider,
    Flex,
    Skeleton,
} from "@mantine/core";
import {
    FaUser, FaBuilding, FaEnvelope,
} from "react-icons/fa";
import { useAuth0 } from "@auth0/auth0-react";
import Cookies from "js-cookie";
import { useUpdateEmailMarketing } from "hooks/useProfile";
import { usePermission } from "hooks/usePermission";
import HeaderTitle from "components/ui/Header/HeaderTitle";

interface ProfileData {
    firstName: string;
    lastName: string;
    email: string;
    organization: string;
    userId: string;
}

const Profile: React.FC = () => {
    document.title = "Profile";

    const { user, isLoading } = useAuth0();
    const [profileData, setProfileData] = useState<ProfileData | null>(null);
    const [oemPartnerNewsEnabled, setOemPartnerNewsEnabled] = useState<boolean>(false);
    const [oemTechnicalUpdatesEnabled, setOemTechnicalUpdatesEnabled] = useState<boolean>(false);
    const [isUpdatingPartnerNews, setIsUpdatingPartnerNews] = useState<boolean>(false);
    const [isUpdatingTechnicalUpdates, setIsUpdatingTechnicalUpdates] = useState<boolean>(false);

    const { mutate: updateEmailMarketing } = useUpdateEmailMarketing();
    const { refetch: refetchPermissions, data: permissionData } = usePermission();

    const ProfileSkeleton = () => (
        <Container size={800} pb="xl" px={{ base: "xs", sm: "md" }}>
            <Skeleton height={32} width={200} mb="xl" />

            <Paper p={{ base: "md", sm: "xl" }} radius="md" withBorder>
                <Stack gap="lg">
                    <div>
                        <Group gap="sm" mb="md">
                            <Skeleton height={20} width={20} />
                            <Skeleton height={24} width={180} />
                        </Group>

                        <Stack gap="md">
                            <Flex
                                direction={{ base: 'column', sm: 'row' }}
                                gap="md"
                            >
                                <div style={{ flex: 1 }}>
                                    <Skeleton height={16} width={80} mb={8} />
                                    <Skeleton height={20} width="100%" />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <Skeleton height={16} width={80} mb={8} />
                                    <Skeleton height={20} width="100%" />
                                </div>
                            </Flex>

                            <div>
                                <Skeleton height={16} width={100} mb={8} />
                                <Group gap="xs">
                                    <Skeleton height={16} width={16} />
                                    <Skeleton height={20} width={200} />
                                </Group>
                            </div>
                        </Stack>
                    </div>

                    <Divider />

                    <div>
                        <Group gap="sm" mb="md">
                            <Skeleton height={20} width={20} />
                            <Skeleton height={24} width={120} />
                        </Group>
                        <Skeleton height={20} width={250} />
                    </div>

                    <Divider />

                    <div>
                        <Skeleton height={24} width={120} mb="md" />

                        <Stack gap="lg">
                            <Group justify="space-between" align="flex-start" visibleFrom="sm">
                                <div style={{ flex: 1 }}>
                                    <Skeleton height={20} width={180} mb={8} />
                                    <Skeleton height={16} width={300} />
                                </div>
                                <Skeleton height={28} width={48} />
                            </Group>

                            <Group justify="space-between" align="flex-start" visibleFrom="sm">
                                <div style={{ flex: 1 }}>
                                    <Skeleton height={20} width={180} mb={8} />
                                    <Skeleton height={16} width={300} />
                                </div>
                                <Skeleton height={28} width={48} />
                            </Group>

                            <Stack gap="md" hiddenFrom="sm">
                                <div>
                                    <Skeleton height={20} width={180} mb={8} />
                                    <Skeleton height={16} width={300} mb="md" />
                                </div>
                                <Skeleton height={28} width={150} />
                            </Stack>

                            <Stack gap="md" hiddenFrom="sm">
                                <div>
                                    <Skeleton height={20} width={180} mb={8} />
                                    <Skeleton height={16} width={300} mb="md" />
                                </div>
                                <Skeleton height={28} width={150} />
                            </Stack>
                        </Stack>
                    </div>
                </Stack>
            </Paper>
        </Container>
    );

    useEffect(() => {
        if (user) {
            const fullName = Cookies.get("user_name") || "";
            const nameParts = fullName.split(" ");
            const firstName = nameParts[0] || "";
            const lastName = nameParts.slice(1).join(" ") || "";

            setProfileData({
                firstName,
                lastName,
                email: user.email || "",
                organization: Cookies.get("accountName") || "",
                userId: user.sub || "",
            });


            setOemPartnerNewsEnabled(Cookies.get("isMarketingNewsLetterRecipient") === "true");
            setOemTechnicalUpdatesEnabled(Cookies.get("isEngineeringNewsLetterRecipient") === "true");
        }
    }, [user]);

    useEffect(() => {
        if (permissionData) {
            const marketingRecipient = permissionData.isMarketingNewsLetterRecipient === true ||
                permissionData.isMarketingNewsLetterRecipient === "true";
            const engineeringRecipient = permissionData.isEngineeringNewsLetterRecipient === true ||
                permissionData.isEngineeringNewsLetterRecipient === "true";

            setOemPartnerNewsEnabled(marketingRecipient);
            setOemTechnicalUpdatesEnabled(engineeringRecipient);
        }
    }, [permissionData]);

    const handleOemPartnerNewsToggle = async (checked: boolean) => {
        setIsUpdatingPartnerNews(true);

        try {
            updateEmailMarketing({
                isMarketingNewsLetterRecipient: checked,
                isEngineeringNewsLetterRecipient: oemTechnicalUpdatesEnabled
            }, {
                onSuccess: () => {
                    setOemPartnerNewsEnabled(checked);
                    setIsUpdatingPartnerNews(false);
                    refetchPermissions();
                },
                onError: (error) => {
                    setIsUpdatingPartnerNews(false);
                    console.error("Failed to update OEM Partner news preference:", error);
                }
            });
        } catch (error) {
            setIsUpdatingPartnerNews(false);
            console.error("Failed to update OEM Partner news preference:", error);
        }
    };

    const handleOemTechnicalUpdatesToggle = async (checked: boolean) => {
        setIsUpdatingTechnicalUpdates(true);

        try {
            updateEmailMarketing({
                isMarketingNewsLetterRecipient: oemPartnerNewsEnabled,
                isEngineeringNewsLetterRecipient: checked
            }, {
                onSuccess: () => {
                    setOemTechnicalUpdatesEnabled(checked);
                    setIsUpdatingTechnicalUpdates(false);
                    refetchPermissions();
                },
                onError: (error) => {
                    setIsUpdatingTechnicalUpdates(false);
                    console.error("Failed to update OEM Technical updates preference:", error);
                }
            });
        } catch (error) {
            setIsUpdatingTechnicalUpdates(false);
            console.error("Failed to update OEM Technical updates preference:", error);
        }
    };

    const SubscriptionToggle = ({
        title,
        description,
        checked,
        onChange,
        disabled = false
    }: {
        title: string;
        description: string;
        checked: boolean;
        onChange: (checked: boolean) => void;
        disabled?: boolean;
    }) => (
        <Flex
            direction={{ base: 'column', sm: 'row' }}
            justify={{ base: 'flex-start', sm: 'space-between' }}
            align={{ base: 'flex-start', sm: 'flex-start' }}
            gap="md"
        >
            <div style={{ flex: 1 }}>
                <Text fw={500} mb={4}>
                    {title}
                </Text>
                <Text size="sm" c="dimmed">
                    {description}
                </Text>
            </div>
            <Switch
                checked={checked}
                onChange={(event) => onChange(event.currentTarget.checked)}
                size="md"
                disabled={disabled}
            />
        </Flex>
    );

    if (isLoading || !profileData) {
        return <ProfileSkeleton />;
    }

    return (
        <Container size={800} pb="xl" px={{ base: "xs", sm: "md" }}>
            <HeaderTitle title="Your Profile" />

            <Paper p={{ base: "md", sm: "xl" }} radius="md" withBorder>
                <Stack gap="lg">
                    <div>
                        <Group gap="sm" mb="md">
                            <FaUser size={20} />
                            <Title order={3}>Personal Information</Title>
                        </Group>

                        <Stack gap="md">
                            <Flex
                                direction={{ base: 'column', sm: 'row' }}
                                gap="md"
                            >
                                <div style={{ flex: 1 }}>
                                    <Text size="sm" fw={500} c="dimmed" mb={4}>
                                        First Name
                                    </Text>
                                    <Text size="md">{profileData.firstName}</Text>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <Text size="sm" fw={500} c="dimmed" mb={4}>
                                        Last Name
                                    </Text>
                                    <Text size="md">{profileData.lastName}</Text>
                                </div>
                            </Flex>

                            <div>
                                <Text size="sm" fw={500} c="dimmed" mb={4}>
                                    Email Address
                                </Text>
                                <Group gap="xs">
                                    <FaEnvelope size={16} />
                                    <Text size="md">{profileData.email}</Text>
                                </Group>
                            </div>
                        </Stack>
                    </div>

                    <Divider />

                    <div>
                        <Group gap="sm" mb="md">
                            <FaBuilding size={20} />
                            <Title order={3}>Organization</Title>
                        </Group>

                        <div>
                            <Text size="md">{profileData.organization}</Text>
                        </div>
                    </div>

                    <Divider />

                    <div>
                        <Title order={3} mb="md">
                            Email Subscriptions
                        </Title>

                        <Stack gap="lg">
                            <SubscriptionToggle
                                title="OEM Partner News Letters"
                                description="Dante product information updates for marketing and product teams"
                                checked={oemPartnerNewsEnabled}
                                onChange={handleOemPartnerNewsToggle}
                                disabled={isUpdatingPartnerNews || isUpdatingTechnicalUpdates}
                            />

                            <SubscriptionToggle
                                title="OEM Technical Updates"
                                description="Product feature releases and firmware updates for engineering teams"
                                checked={oemTechnicalUpdatesEnabled}
                                onChange={handleOemTechnicalUpdatesToggle}
                                disabled={isUpdatingPartnerNews || isUpdatingTechnicalUpdates}
                            />
                        </Stack>
                    </div>
                </Stack>
            </Paper>
        </Container>
    );
};

export default Profile;
